import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { TextLayer } from 'pdfjs-dist';
import 'pdfjs-dist/web/pdf_viewer.css';
import { getPdfWorkerBlobUrl } from '../utils/pdfWorkerBlob';
import { IonSpinner, IonIcon, IonButton } from '@ionic/react';
import {
  alertCircleOutline, downloadOutline, shareOutline, addOutline, removeOutline,
  pencilOutline, textOutline, handLeftOutline, arrowUndoOutline,
  chatbubbleOutline, closeCircleOutline, refreshOutline, contractOutline,
  gridOutline, expandOutline,
} from 'ionicons/icons';
import styles from './PdfViewerMobile.module.scss';
import { useTranslation } from 'react-i18next';

pdfjsLib.GlobalWorkerOptions.workerSrc = getPdfWorkerBlobUrl();

const MIN_ZOOM = 25;
const MAX_ZOOM = 400;
const HL_COLORS = ['rgba(255,213,0,0.42)', 'rgba(0,200,80,0.35)', 'rgba(255,70,70,0.32)', 'rgba(0,140,255,0.32)'];

interface Highlight {
  id: string; pageNum: number;
  rects: Array<{ x: number; y: number; w: number; h: number }>;
  color: string; zoomAtSave: number;
}
interface TextNote {
  id: string; pageNum: number; x: number; y: number;
  width: number; height: number; text: string; editing: boolean; zoomAtSave: number;
}

function mergeRects(raw: Array<{ x: number; y: number; w: number; h: number }>) {
  if (!raw.length) return [];
  const sorted = [...raw].sort((a, b) => a.y - b.y);
  const rows: Array<typeof raw> = [];
  for (const r of sorted) {
    const mid = r.y + r.h / 2;
    const row = rows.find(rw => Math.abs(rw[0].y + rw[0].h / 2 - mid) < r.h * 0.35);
    if (row) row.push(r); else rows.push([r]);
  }
  return rows.map(row => ({
    x: Math.min(...row.map(r => r.x)), y: Math.min(...row.map(r => r.y)),
    w: Math.max(...row.map(r => r.x + r.w)) - Math.min(...row.map(r => r.x)),
    h: Math.max(...row.map(r => r.h)),
  }));
}

interface PdfViewerMobileProps {
  dataUrl: string | null;
  isLoading: boolean;
  fileName?: string;
  onFullscreenChange?: (isFullscreen: boolean) => void;
}

const PdfViewerMobile: React.FC<PdfViewerMobileProps> = ({ dataUrl, isLoading, fileName = 'document.pdf', onFullscreenChange }) => {
  const { t } = useTranslation();
  const [pdfDoc, setPdfDoc] = useState<pdfjsLib.PDFDocumentProxy | null>(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [zoom, setZoom] = useState(100);
  const [rotation, setRotation] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [rendering, setRendering] = useState(false);
  const [viewMode, setViewMode] = useState<'select' | 'highlight' | 'pan' | 'note'>('select');
  const [activeColor, setActiveColor] = useState(HL_COLORS[0]);
  const [highlights, setHighlights] = useState<Highlight[]>([]);
  const [notes, setNotes] = useState<TextNote[]>([]);
  const [history, setHistory] = useState<Array<{ type: 'highlight' | 'note'; id: string }>>([]);
  const [hlDrag, setHlDrag] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const [showThumbs, setShowThumbs] = useState(false);
  const [thumbs, setThumbs] = useState<{ [key: number]: string }>({});
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    onFullscreenChange?.(isFullscreen);
  }, [isFullscreen, onFullscreenChange]);

  const containerRef = useRef<HTMLDivElement>(null);
  const pageWrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textLayerRef = useRef<HTMLDivElement>(null);
  const linkLayerRef = useRef<HTMLDivElement>(null);
  const renderTaskRef = useRef<ReturnType<pdfjsLib.PDFPageProxy['render']> | null>(null);
  const tlRef = useRef<{ cancel: () => void } | null>(null);
  const textCacheRef = useRef<{ items: Array<{ str: string; width: number; height: number; transform: number[] }> } | null>(null);
  const pinchRef = useRef<number | null>(null);
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const hlDragStartRef = useRef<{ x: number; y: number } | null>(null);
  const noteDragRef = useRef<{ id: string; startMouseX: number; startMouseY: number; startNoteX: number; startNoteY: number; scale: number } | null>(null);
  const pageInputRef = useRef<HTMLInputElement>(null);

  const clampZoom = (v: number) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, v));

  useEffect(() => {
    if (!dataUrl) return;
    const load = async () => {
      try {
        setError(null); setPdfDoc(null); setPageNum(1);
        setHighlights([]); setNotes([]); setHistory([]); textCacheRef.current = null; setThumbs({});
        const b64 = dataUrl.startsWith('data:application/pdf;base64,') ? dataUrl.slice('data:application/pdf;base64,'.length) : dataUrl;
        const bin = atob(b64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        const pdf = await pdfjsLib.getDocument({ data: bytes.buffer }).promise;
        if (containerRef.current) {
          try {
            const pg = await pdf.getPage(1);
            const vp = pg.getViewport({ scale: 1 });
            const w = containerRef.current.clientWidth - 16;
            if (w > 0 && vp.width > 0) setZoom(clampZoom(Math.round((w / vp.width) * 100)));
          } catch { /* */ }
        }
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
      } catch (e) {
        console.error('[PdfViewerMobile] load:', e);
        setError(t('components.PdfViewerMobile.failedToLoadPdf'));
      }
    };
    load();
  }, [dataUrl, t]);

  const renderThumbnail = useCallback(async (pn: number) => {
    if (!pdfDoc || thumbs[pn]) return;
    try {
      const pg = await pdfDoc.getPage(pn);
      const vp = pg.getViewport({ scale: 0.15 });
      const canvas = document.createElement('canvas');
      canvas.width = vp.width;
      canvas.height = vp.height;
      const ctx = canvas.getContext('2d')!;
      await pg.render({ canvas, canvasContext: ctx, viewport: vp }).promise;
      setThumbs(prev => ({ ...prev, [pn]: canvas.toDataURL('image/jpeg', 0.8) }));
    } catch { /* */ }
  }, [pdfDoc, thumbs]);

  useEffect(() => {
    if (!showThumbs || !pdfDoc) return;
    Promise.resolve().then(() => {
      for (let i = 1; i <= Math.min(numPages, 50); i++) renderThumbnail(i);
    });
  }, [showThumbs, pdfDoc, numPages, renderThumbnail]);

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current || zoom <= 0) return;
    textCacheRef.current = null;

    const render = async () => {
      if (renderTaskRef.current) { renderTaskRef.current.cancel(); renderTaskRef.current = null; }
      if (tlRef.current) { tlRef.current.cancel(); tlRef.current = null; }
      if (textLayerRef.current) textLayerRef.current.innerHTML = '';
      if (linkLayerRef.current) linkLayerRef.current.innerHTML = '';

      setRendering(true);
      try {
        const pg = await pdfDoc.getPage(pageNum);
        const canvas = canvasRef.current!;
        const dpr = window.devicePixelRatio || 1;
        const cssScale = zoom / 100;
        const cssVp = pg.getViewport({ scale: cssScale, rotation });
        const canVp = pg.getViewport({ scale: cssScale * dpr, rotation });

        canvas.width = canVp.width;
        canvas.height = canVp.height;
        canvas.style.width = `${cssVp.width}px`;
        canvas.style.height = `${cssVp.height}px`;

        const ctx = canvas.getContext('2d')!;
        const task = pg.render({ canvas, canvasContext: ctx, viewport: canVp });
        renderTaskRef.current = task;
        await task.promise;
        renderTaskRef.current = null;

        if (!textCacheRef.current) {
          try {
            const stream = pg.streamTextContent();
            const reader = stream.getReader();
            const items: Array<{ str: string; width: number; height: number; transform: number[] }> = [];
            try { while (true) { const { done, value } = await reader.read(); if (done) break; if (value?.items) items.push(...value.items); } }
            finally { reader.releaseLock(); }
            textCacheRef.current = { items };
          } catch { textCacheRef.current = { items: [] }; }
        }

        if (textLayerRef.current) {
          const tl = new TextLayer({ textContentSource: pg.streamTextContent(), container: textLayerRef.current, viewport: cssVp });
          tlRef.current = tl;
          await tl.render();
          tlRef.current = null;
        }

        if (linkLayerRef.current) {
          linkLayerRef.current.innerHTML = '';
          try {
            const anns = await pg.getAnnotations({ intent: 'display' });
            for (const ann of anns) {
              if (ann.subtype !== 'Link') continue;
              const [x1, y1, x2, y2] = cssVp.convertToViewportRectangle(ann.rect);
              const a = document.createElement('a');
              a.style.cssText = `position:absolute;left:${Math.min(x1,x2)}px;top:${Math.min(y1,y2)}px;width:${Math.abs(x2-x1)}px;height:${Math.abs(y2-y1)}px`;
              if (ann.url) { a.href = ann.url; a.target = '_blank'; a.rel = 'noopener noreferrer'; }
              else if (ann.dest) { a.href = '#'; a.addEventListener('click', e => { e.preventDefault(); if (Array.isArray(ann.dest) && ann.dest[0]?.num != null) pdfDoc.getPageIndex(ann.dest[0]).then(i => setPageNum(i + 1)).catch(() => {}); }); }
              else continue;
              linkLayerRef.current.appendChild(a);
            }
          } catch { /* */ }
        }
      } catch (e) {
        if (e instanceof Error && e.name === 'RenderingCancelledException') return;
        console.error('[PdfViewerMobile] render:', e);
      } finally { setRendering(false); }
    };
    render();
  }, [pdfDoc, pageNum, zoom, rotation]);

  const goToPage = useCallback((p: number) => {
    if (p >= 1 && p <= numPages) setPageNum(p);
    if (pageInputRef.current) pageInputRef.current.value = String(p >= 1 && p <= numPages ? p : pageNum);
  }, [numPages, pageNum]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    let touchStartX = 0;
    const onStart = (e: TouchEvent) => {
      touchStartX = e.touches[0].clientX;
      if (e.touches.length === 2) {
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        pinchRef.current = Math.sqrt(dx*dx+dy*dy);
      }
    };
    const onMove = (e: TouchEvent) => {
      if (e.touches.length === 2 && pinchRef.current !== null) {
        e.preventDefault();
        const dx = e.touches[0].clientX - e.touches[1].clientX;
        const dy = e.touches[0].clientY - e.touches[1].clientY;
        const dist = Math.sqrt(dx*dx+dy*dy);
        setZoom(z => clampZoom(Math.round(z * (dist / pinchRef.current!))));
        pinchRef.current = dist;
      }
    };
    const onEnd = (e: TouchEvent) => {
      pinchRef.current = null;
      if (isFullscreen && e.changedTouches.length === 1) {
        const touchEndX = e.changedTouches[0].clientX;
        const diff = touchStartX - touchEndX;
        if (Math.abs(diff) > 50) {
          if (diff > 0) goToPage(pageNum + 1);
          else goToPage(pageNum - 1);
        }
      }
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
    };
  }, [isFullscreen, pageNum, numPages, goToPage]);

  const applyHighlight = useCallback(async (drag: { x: number; y: number; w: number; h: number }) => {
    if (!pdfDoc || !textCacheRef.current) return;
    try {
      const pg = await pdfDoc.getPage(pageNum);
      const vp = pg.getViewport({ scale: zoom / 100, rotation });
      const rawRects: Array<{ x: number; y: number; w: number; h: number }> = [];
      for (const rawItem of textCacheRef.current.items) {
        const item = rawItem as { str: string; width: number; height: number; transform: number[] };
        if (!item.str.trim() || !item.transform) continue;
        const [,,,,tx,ty] = item.transform;
        const [vx,vy] = vp.convertToViewportPoint(tx, ty);
        const iw = item.width * (zoom/100); const ih = item.height * (zoom/100);
        const rx = vx; const ry = vy - ih; const rh = ih * 1.15;
        if (rx+iw < drag.x || rx > drag.x+drag.w || ry+rh < drag.y || ry > drag.y+drag.h) continue;
        rawRects.push({ x: rx, y: ry, w: iw, h: rh });
      }
      if (!rawRects.length) return;
      const id = Math.random().toString(36).slice(2);
      setHighlights(prev => [...prev, { id, pageNum, rects: mergeRects(rawRects), color: activeColor, zoomAtSave: zoom }]);
      setHistory(prev => [...prev, { type: 'highlight', id }]);
    } catch (e) { console.error('[PdfViewerMobile] highlight:', e); }
  }, [pdfDoc, pageNum, zoom, rotation, activeColor]);

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    if (viewMode === 'pan') { e.preventDefault(); dragStartRef.current = { x: e.clientX, y: e.clientY }; setIsDragging(true); return; }
    if (viewMode === 'highlight' && pageWrapRef.current) {
      const bcr = pageWrapRef.current.getBoundingClientRect();
      hlDragStartRef.current = { x: e.clientX - bcr.left, y: e.clientY - bcr.top };
      setHlDrag({ x: e.clientX - bcr.left, y: e.clientY - bcr.top, w: 0, h: 0 });
    }
  }, [viewMode]);

  const onMouseMove = useCallback((e: React.MouseEvent) => {
    if (isDragging && dragStartRef.current && containerRef.current) {
      e.preventDefault();
      containerRef.current.scrollLeft -= e.clientX - dragStartRef.current.x;
      containerRef.current.scrollTop -= e.clientY - dragStartRef.current.y;
      dragStartRef.current = { x: e.clientX, y: e.clientY }; return;
    }
    if (hlDragStartRef.current && pageWrapRef.current) {
      const bcr = pageWrapRef.current.getBoundingClientRect();
      const cx = e.clientX - bcr.left; const cy = e.clientY - bcr.top;
      setHlDrag({ x: Math.min(hlDragStartRef.current.x, cx), y: Math.min(hlDragStartRef.current.y, cy), w: Math.abs(cx - hlDragStartRef.current.x), h: Math.abs(cy - hlDragStartRef.current.y) });
    }
  }, [isDragging]);

  const onMouseUp = useCallback(() => {
    if (hlDrag && hlDrag.w > 5 && hlDrag.h > 5) applyHighlight(hlDrag);
    hlDragStartRef.current = null; setHlDrag(null);
    dragStartRef.current = null; setIsDragging(false);
  }, [hlDrag, applyHighlight]);

  const handlePageClick = useCallback((e: React.MouseEvent) => {
    if (viewMode !== 'note' || !pageWrapRef.current) return;
    e.stopPropagation();
    const bcr = pageWrapRef.current.getBoundingClientRect();
    setNotes(prev => [...prev, { id: Math.random().toString(36).slice(2), pageNum, x: e.clientX - bcr.left, y: e.clientY - bcr.top, width: 240, height: 120, text: '', editing: true, zoomAtSave: zoom }]);
  }, [viewMode, pageNum, zoom]);

  const commitNote = (id: string) => setNotes(prev => prev.flatMap(n => { if (n.id !== id) return [n]; if (!n.text.trim()) return []; setHistory(h => [...h, { type: 'note', id }]); return [{ ...n, editing: false }]; }));

  const undoLast = useCallback(() => {
    setHistory(prev => { if (!prev.length) return prev; const last = prev[prev.length - 1]; if (last.type === 'highlight') setHighlights(h => h.filter(x => x.id !== last.id)); else setNotes(n => n.filter(x => x.id !== last.id)); return prev.slice(0, -1); });
  }, []);

  const handleFitWidth = useCallback(() => {
    if (!pdfDoc) return;
    const fit = async () => {
      try { const pg = await pdfDoc.getPage(pageNum); const vp = pg.getViewport({ scale: 1 }); const w = containerRef.current?.clientWidth || 600; if (w > 0 && vp.width > 0) setZoom(clampZoom(Math.round((w - 16) / vp.width * 100))); } catch {}
    };
    fit();
  }, [pdfDoc, pageNum]);


  useEffect(() => {
    const onMove = (e: MouseEvent) => { const d = noteDragRef.current; if (!d) return; const dx = e.clientX - d.startMouseX; const dy = e.clientY - d.startMouseY; setNotes(prev => prev.map(n => n.id === d.id ? { ...n, x: (d.startNoteX + dx) / d.scale, y: (d.startNoteY + dy) / d.scale } : n)); };
    const onUp = () => { noteDragRef.current = null; };
    document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp);
    return () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
  }, []);

  const handleDownload = useCallback(() => {
    if (!dataUrl) return;
    const a = document.createElement('a'); a.href = dataUrl; a.download = fileName;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  }, [dataUrl, fileName]);

  const handleShare = useCallback(async () => {
    if (!dataUrl || !('share' in navigator)) return;
    try { const b64 = dataUrl.startsWith('data:application/pdf;base64,') ? dataUrl.slice('data:application/pdf;base64,'.length) : dataUrl; const bin = atob(b64); const bytes = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i); await navigator.share({ files: [new File([new Blob([bytes], { type: 'application/pdf' })], fileName, { type: 'application/pdf' })], title: fileName }); } catch { /* */ }
  }, [dataUrl, fileName]);

  if (isLoading) return <div className={styles.center}><IonSpinner name="bubbles" color="primary" /><span>{t('components.PdfViewerMobile.loadingPdf')}</span></div>;
  if (error) return <div className={styles.center}><IonIcon icon={alertCircleOutline} className={styles.errorIcon} /><span>{error}</span></div>;
  if (!dataUrl) return null;

  const pageHls = highlights.filter(h => h.pageNum === pageNum);
  const pageNotes = notes.filter(n => n.pageNum === pageNum);

  return (
    <div className={`${styles.wrapper} ${isFullscreen ? styles.hideUi : ''}`}>
      {isFullscreen && (
        <button
          className={styles.floatingExitFullscreen}
          onClick={() => setIsFullscreen(false)}
          title={t('components.PdfViewerMobile.exitFullscreen')}
        >
          <IonIcon icon={contractOutline} />
        </button>
      )}
      <div className={styles.toolbar}>
        <div className={styles.tbLeft}>
          <IonButton fill={showThumbs ? 'solid' : 'clear'} size="small" onClick={() => setShowThumbs(v => !v)}><IonIcon icon={gridOutline} slot="icon-only" /></IonButton>
          <div className={styles.tbMenuWrap}>
            <IonButton fill={viewMode !== 'select' ? 'solid' : 'clear'} size="small" onClick={() => setShowTools(v => !v)}><IonIcon icon={viewMode === 'pan' ? handLeftOutline : viewMode === 'note' ? chatbubbleOutline : pencilOutline} slot="icon-only" />{viewMode === 'highlight' && <span className={styles.hlColorBar} style={{ background: activeColor.replace(/[\d.]+\)$/, '1)') }} />}</IonButton>
            {showTools && (
              <div className={styles.toolsDropdown} onMouseLeave={() => setShowTools(false)}>
                <button className={`${styles.toolsItem} ${viewMode === 'select' ? styles.active : ''}`} onClick={() => { setViewMode('select'); setShowTools(false); }}><IonIcon icon={textOutline} /><span>{t('components.PdfViewerMobile.selectText')}</span></button>
                <button className={`${styles.toolsItem} ${viewMode === 'highlight' ? styles.active : ''}`} onClick={() => setViewMode('highlight')}><IonIcon icon={pencilOutline} /><span>{t('components.PdfViewerMobile.highlight')}</span><span className={styles.hlMenuDot} style={{ background: activeColor.replace(/[\d.]+\)$/, '1)') }} /></button>
                <div className={styles.hlMenuColors}>{HL_COLORS.map(color => <button key={color} className={`${styles.hlColorBtn} ${color === activeColor ? styles.hlColorActive : ''}`} style={{ background: color.replace(/[\d.]+\)$/, '1)') }} onClick={() => setActiveColor(color)} />)}</div>
                <button className={styles.toolsItem} onClick={() => { undoLast(); setShowTools(false); }} disabled={history.length === 0}><IonIcon icon={arrowUndoOutline} /><span>{t('components.PdfViewerMobile.eraseLast')}</span></button>
                <button className={`${styles.toolsItem} ${viewMode === 'pan' ? styles.active : ''}`} onClick={() => { setViewMode('pan'); setShowTools(false); }}><IonIcon icon={handLeftOutline} /><span>{t('components.PdfViewerMobile.pan')}</span></button>
                <button className={`${styles.toolsItem} ${viewMode === 'note' ? styles.active : ''}`} onClick={() => { setViewMode('note'); setShowTools(false); }}><IonIcon icon={chatbubbleOutline} /><span>{t('components.PdfViewerMobile.addTextNote')}</span></button>
              </div>
            )}
          </div>
        </div>

        <div className={styles.tbMid}>
          <IonButton fill="clear" size="small" onClick={() => setZoom(z => clampZoom(z - 15))} disabled={zoom <= MIN_ZOOM}><IonIcon icon={removeOutline} slot="icon-only" /></IonButton>
          <IonButton fill="clear" size="small" onClick={() => setZoom(z => clampZoom(z + 15))} disabled={zoom >= MAX_ZOOM}><IonIcon icon={addOutline} slot="icon-only" /></IonButton>
          <IonButton fill="clear" size="small" onClick={handleFitWidth}><IonIcon icon={contractOutline} slot="icon-only" /></IonButton>
          {numPages > 0 && (<><span className={styles.tbDivider} /><input ref={pageInputRef} className={styles.pageInput} type="number" min={1} max={numPages} defaultValue={pageNum} onFocus={e => e.currentTarget.select()} onBlur={e => goToPage(parseInt(e.currentTarget.value, 10) || pageNum)} onKeyDown={e => { if (e.key === 'Enter') { goToPage(parseInt(e.currentTarget.value, 10) || pageNum); e.currentTarget.blur(); } if (e.key === 'Escape') { e.currentTarget.value = String(pageNum); e.currentTarget.blur(); } }} /><span className={styles.pageOf}>{t('components.PdfViewerMobile.of', { numPages })}</span></>)}
        </div>

        <div className={styles.tbRight}>
          <IonButton fill="clear" size="small" onClick={() => setIsFullscreen(v => !v)}><IonIcon icon={isFullscreen ? contractOutline : expandOutline} slot="icon-only" /></IonButton>
          <IonButton fill="clear" size="small" onClick={() => setRotation(r => (r + 90) % 360)}><IonIcon icon={refreshOutline} slot="icon-only" /></IonButton>
          <IonButton fill="clear" size="small" onClick={handleDownload}><IonIcon icon={downloadOutline} slot="icon-only" /></IonButton>
          {'share' in navigator && <IonButton fill="clear" size="small" onClick={handleShare}><IonIcon icon={shareOutline} slot="icon-only" /></IonButton>}
        </div>
      </div>

      <div className={styles.contentArea}>
        {showThumbs && (
          <div className={styles.thumbnailPanel}>
            <div className={styles.thumbnailHeader}>
              <span>{t('components.PdfViewerMobile.pages')}</span>
              <button className={styles.closeThumbnails} onClick={() => setShowThumbs(false)}><IonIcon icon={closeCircleOutline} /></button>
            </div>
            <div className={styles.thumbnailGrid}>
              {Array.from({ length: numPages }, (_, i) => (
                <div key={i + 1} className={`${styles.thumbnail} ${pageNum === i + 1 ? styles.active : ''}`} onClick={() => goToPage(i + 1)} title={t('components.PdfViewerMobile.page', { v1: i + 1 })}>
                  {thumbs[i + 1] ? <img src={thumbs[i + 1]} alt={t('components.PdfViewerMobile.page', { v1: i + 1 })} /> : <div className={styles.thumbnailLoading}><span>{i + 1}</span></div>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div ref={containerRef} className={`${styles.scrollArea} ${isDragging ? styles.grabbing : viewMode === 'pan' ? styles.panMode : viewMode === 'highlight' ? styles.hlMode : ''}`} onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={onMouseUp}>
          {rendering && <div className={styles.renderOverlay}><IonSpinner name="dots" color="medium" /></div>}

          <div ref={pageWrapRef} className={styles.pageWrap} onClick={handlePageClick}>
            <canvas ref={canvasRef} />

            <div className={styles.hlLayer}>
              {pageHls.flatMap(h => { const s = zoom / h.zoomAtSave; return h.rects.map((r, ri) => <div key={`${h.id}-${ri}`} className={styles.hlRect} style={{ left: r.x*s, top: r.y*s, width: r.w*s, height: r.h*s, background: h.color }} />); })}
              {hlDrag && <div className={styles.hlPreview} style={{ left: hlDrag.x, top: hlDrag.y, width: hlDrag.w, height: hlDrag.h, background: activeColor }} />}
            </div>

            {pageNotes.map(note => { const ns = zoom / note.zoomAtSave; return (
              <div key={note.id} className={styles.noteOverlay} style={{ left: note.x*ns, top: note.y*ns, width: note.editing ? note.width*ns : 'fit-content', height: note.editing ? note.height*ns : 'fit-content' }} onClick={e => e.stopPropagation()}>
                {note.editing ? (
                  <><div className={styles.noteDragHandle} onMouseDown={e => { e.stopPropagation(); e.preventDefault(); noteDragRef.current = { id: note.id, startMouseX: e.clientX, startMouseY: e.clientY, startNoteX: note.x*ns, startNoteY: note.y*ns, scale: ns }; }}><span>≡</span><button className={styles.noteClose} onMouseDown={e => { e.stopPropagation(); e.preventDefault(); setNotes(prev => prev.filter(n => n.id !== note.id)); }}><IonIcon icon={closeCircleOutline} /></button></div><textarea className={styles.noteArea} value={note.text} autoFocus placeholder={t('components.PdfViewerMobile.typeNote')} onChange={e => setNotes(prev => prev.map(n => n.id === note.id ? { ...n, text: e.target.value } : n))} onBlur={() => commitNote(note.id)} onKeyDown={e => { if (e.key === 'Escape') { e.preventDefault(); commitNote(note.id); } }} /></>
                ) : (
                  <div className={styles.noteBody} onDoubleClick={() => setNotes(prev => prev.map(n => n.id === note.id ? { ...n, editing: true } : n))}>{note.text}</div>
                )}
              </div>
            ); })}

            <div ref={textLayerRef} className="textLayer" style={{ pointerEvents: viewMode === 'select' ? 'auto' : 'none', userSelect: viewMode === 'select' ? 'text' : 'none' }} />
            <div ref={linkLayerRef} className={styles.linkLayer} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PdfViewerMobile;
